<script setup>
import { ref, computed } from 'vue'
import dayjs from 'dayjs'
import ja from 'dayjs/locale/ja'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.tz.setDefault('Asia/Tokyo')
dayjs.locale(ja)

const props = defineProps({
  center: {
    type: Array,
    required: true
  },
  id: {
    type: String,
    required: true
  }
})
const { $isWebViewFlag: isWebViewFlag } = useNuxtApp()
const { data: windJson } = await useFetch('/api/golf/wind', { query: { id: props.id } })
const windMap = shallowRef(null)
const formatWindDirection = computed(() => {
  return Math.floor((markerWindDirection.value / 22.5) + 15.5) % 16 + 1
})
const selectedTime = ref(windJson.value?.list?.[0]?.time || '')
const binaryPath = computed(() => windJson.value?.list?.find((item) => item.time === selectedTime.value)?.path || '')
const { initMap, markerWindSpeed, markerWindDirection, showCenterIcon, isProgrammaticMove, updateData } = useWindMap()
function handleGeolocate() {
  isProgrammaticMove.value = true
  showCenterIcon.value = false
}

watch(binaryPath, (newPath) => {
  if (newPath) {
    updateData(newPath, windJson.value.meta)
  }
})

onMounted(async () => {
  await initMap(windMap.value, props.center)
  if (binaryPath.value) {
    updateData(binaryPath.value, windJson.value.meta)
  }
  window.addEventListener('custom-geolocate', handleGeolocate)
})

onUnmounted(() => {
  window.removeEventListener('custom-geolocate', handleGeolocate)
})
</script>

<template>
  <section class="golfWind">
    <CommonSectionTitle
      title="風の強さをシミュレーション"
      :text="`${dayjs.tz(selectedTime, 'YYYYMMDDHHmm', 'Asia/Tokyo').format('M/D (ddd) HH:mm')}予想`"
    />
    <div class="content">
      <div class="wind">
        <div
          ref="windMap"
          class="map"
        />
        <div
          v-if="showCenterIcon"
          class="center"
        >
          <img
            src="https://gvs.weathernews.jp/golf/image/map/golf_icon1_50.svg"
            alt=""
          >
        </div>
        <div class="legend">
          <img
            src="https://gvs.weathernews.jp/golf/image/common/wind_hanrei.svg"
            alt=""
          >
        </div>
        <template v-if="markerWindDirection&&markerWindSpeed">
          <div class="info">
            <div class="dir">
              <div class="dir__icon">
                <svg
                  v-if="markerWindSpeed>0.2"
                  xmlns="http://www.w3.org/2000/svg"
                  width="11"
                  height="15"
                  viewBox="0 0 11 15"
                  :data-rank="setWindRank(markerWindSpeed)"
                  :style="`transform: rotate(calc(1deg * ${markerWindDirection-180})) scale(1.5)`"
                >
                  <path
                    d="M9.91706 1.33286L5.48529 13.7418L1.05353 1.33286L5.31805 5.17093L5.48529 5.32145L5.65253 5.17093L9.91706 1.33286Z"
                    stroke="#333333"
                    stroke-width="0.5"
                  />
                </svg>
              </div>
              <p class="dir__text">
                {{ markerWindSpeed > 0.2 ? windDirectionNames[formatWindDirection] : '静穏' }}
              </p>
            </div>
            <div class="value">
              <p class="value__text">
                {{ markerWindSpeed.toFixed(1) }}
              </p>
              <p class="value__unit">
                m/s
              </p>
            </div>
          </div>
        </template>
      </div>
      <div
        v-if="isWebViewFlag"
        class="box"
      >
        <GolfWindControl
          v-model:selected-time="selectedTime"
          :time-list="windJson.list"
        />
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.golfWind {
  display: flex;
  flex-direction: column;
  gap: 10px;
  .content {
    display: flex;
    flex-direction: column;
    margin: 0;
    gap: 0;
  }
  .wind {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    aspect-ratio: 4 / 3;
    .map {
      width: 100%;
      height: 100%;
    }
  }
  .center {
    position: absolute;
    top: calc(50% - 15px);
    left: calc(50% - 15px);
    width: 30px;
    height: 30px;
    pointer-events: none;
    img {
      width: 100%;
      height: 100%;
    }
  }
  .legend {
    position: absolute;
    bottom: 5px;
    right: 5px;
    width: 20%;
    aspect-ratio: 113 / 30;
    pointer-events: none;
    img {
      width: 100%;
    }
  }
  .box {
    display: flex;
    flex-direction: column;
    gap: 15px;
    padding: 10px 5px 5px;
    background-color: #eef1f8;
  }
  .info {
    display: flex;
    align-items: center;
    gap: 5px;
    position: absolute;
    top: 5px;
    right: 5px;
    padding: 5px 10px;
    border-radius: 6px;
    background-color: #EEF1F8;
    .dir {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 2px;
      &__icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 30px;
        height: 30px;
        padding: 2px;
        border-radius: 50%;
        background-color: #fff;
        svg {
          &[data-rank='0'] {
            fill: #ffffff;
          }
          &[data-rank='1'] {
            fill: #03aeef;
          }
          &[data-rank='2'] {
            fill: #05ad50;
          }
          &[data-rank='3'] {
            fill: #febf00;
          }
          &[data-rank='4'] {
            fill: #e86200;
          }
          &[data-rank='5'] {
            fill: #fe0000;
          }
        }
      }
      &__text {
        color: #000;
        font-size: 12px;
        line-height: 1em;
      }
    }
    .value {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      &__text {
        color: #000;
        font-size: 20px;
        font-weight: 600;
        line-height: 1em;
      }
      &__unit {
        color: #000;
        font-size: 12px;
        line-height: 1em;
      }
    }
  }
}
.banner {
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 20px;
  .banner__inner {
    width: 100%;
    max-width: 640px;
    aspect-ratio: 64 / 13;
    @media (any-hover: hover) {
      transition: opacity .2s ease-in-out;
      &:hover {
        opacity: .6;
      }
    }
    img {
      width: 100%;
    }
  }
}
@media screen and (min-width: 830px) {
  .golfWind {
    .box {
      padding-inline: 15px;
    }
    .info {
      top: 10px;
      right: 10px;
      gap: 10px;
      padding: 10px 20px;
      .value {
        &__text {
          font-size: 32px;
        }
      }
    }
  }
}

.mapboxgl-ctrl-attrib-button {
  background-color: #007bff;
  color: white;
  border: none;
  padding: 5px 10px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.3s;
}

.mapboxgl-ctrl-attrib-button:hover {
  background-color: #0056b3;
}

.mapboxgl-ctrl-attrib-inner {
  background-color: white;
  padding: 5px;
  border-radius: 5px;
  box-shadow: 0 0 10px rgba(0,0,0,0.1);
}
</style>
